import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Clock, CheckCircle, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { certifiantePrograms } from '../../data/programs';
import { formatPrice } from '../../lib/utils';
import SEO from '../../components/SEO';
import CallbackWidget from '../../components/CallbackWidget';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const advantages = [
  'Partenariat BIT (Bureau International du Travail)',
  'Financement FDFP disponible',
  'Hébergement inclus pour les participants hors Abidjan',
  'Transport organisé',
  'Visites touristiques et team building',
  'Certificat reconnu internationalement'
];

export default function Certifiante() {
  return (
    <div data-testid="certifiante-page">
      <SEO
        title="Formation Certifiante Abidjan - Certificats Professionnels"
        description="Centre de Formation Certifiante CEFAT : certificats specialises pour professionnels et cadres. Leadership, gestion de projet, finance. Eligible financement entreprise a Abidjan."
        keywords="formation certifiante Abidjan, certificat professionnel Cote d'Ivoire, leadership formation Abidjan, gestion de projet Abidjan, formation cadres Abidjan, CEFAT certifiante"
        url="/certifiante"
      />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#003366] to-[#001a33] py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#FFD700]/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-[#FFD700] text-[#003366] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Centre de Formation Certifiante
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                Certificats <span className="text-[#FFD700]">Professionnels</span>
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Des certifications spécialisées pour les professionnels, cadres, ONG et entreprises. 
                Boostez votre carrière avec des compétences reconnues internationalement.
              </p>
              <Link to="/preinscription?center=certifiante">
                <Button className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg">
                  Je me préinscris <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1758519290111-bfbd61b32d49?w=600&h=500&fit=crop"
                alt="Formation certifiante"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-[#FFD700]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#003366] mb-2">
                Avantages Exclusifs
              </h2>
              <p className="text-[#003366]/80">Des offres exceptionnelles pour votre formation</p>
            </div>
            <div className="flex flex-wrap gap-4">
              {advantages.slice(0, 3).map((adv, i) => (
                <span key={i} className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full text-sm font-medium text-[#003366]">
                  <Star size={14} className="text-[#DC143C]" /> {adv}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-gold mb-4">Catalogue</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Nos Certificats Spécialisés
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des formations courtes et intensives pour développer des compétences stratégiques 
              dans votre domaine d'expertise.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifiantePrograms.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl overflow-hidden shadow-md card-hover"
              >
                <div className="h-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500]" />
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#FFD700]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="text-[#997300]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#003366]">{program.name}</h3>
                      <p className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Clock size={14} /> {program.duration}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4">{program.description}</p>

                  {program.partners && (
                    <div className="flex gap-2 mb-4">
                      {program.partners.map((partner, i) => (
                        <span key={i} className="badge badge-blue text-xs">{partner}</span>
                      ))}
                    </div>
                  )}

                  <div className="bg-[#F8FAFC] rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-500 mb-1">Coût</p>
                    <p className="font-heading text-2xl font-bold text-[#003366]">{formatPrice(program.price)}</p>
                  </div>

                  <Link to={`/preinscription?center=certifiante&program=${program.id}`}>
                    <Button className="w-full bg-[#003366] hover:bg-[#002244] text-white rounded-full">
                      S'inscrire <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* All Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-[#003366] mb-4">
              Pourquoi choisir nos certifications ?
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((adv, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 bg-[#F8FAFC] rounded-xl"
              >
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <span className="text-gray-700 font-medium">{adv}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Callback Widget */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <CallbackWidget variant="inline" center="certifiante" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Développez vos compétences stratégiques
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Formations éligibles au financement entreprise et FDFP.
          </p>
          <Link to="/preinscription?center=certifiante">
            <Button className="bg-[#FFD700] text-[#003366] hover:bg-[#FFC000] rounded-full px-10 py-6 text-lg font-bold shadow-lg">
              Je me préinscris maintenant <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
